import { useState, useRef, useEffect } from "react";
import api from "../lib/api";
import { Bot, X, Send, Sparkles } from "lucide-react";

export default function AIAssistant() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([
    { id: "intro", role: "ai", text: "I'm your portfolio analyst. Ask me about occupancy, cash flow, cap rates, delinquencies, or which properties to focus on." },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef();
  const mid = () => (crypto.randomUUID ? crypto.randomUUID() : String(Math.random()));

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, loading]);

  const send = async () => {
    if (!input.trim() || loading) return;
    const q = input.trim();
    setMsgs((m) => [...m, { id: mid(), role: "user", text: q }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/ai/chat", { message: q });
      setMsgs((m) => [...m, { id: mid(), role: "ai", text: data.reply }]);
    } catch (error) {
      console.error("AI chat error:", error);
      setMsgs((m) => [...m, { id: mid(), role: "ai", text: "Sorry, I couldn't process that right now." }]);
    } finally {
      setLoading(false);
    }
  };

  const suggestions = ["Which properties have the best cap rate?", "Summarize my delinquencies", "How is my cash flow trending?"];

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="ai-fab"
        className="fixed bottom-6 right-6 z-40 w-14 h-14 bg-primary text-primary-foreground flex items-center justify-center shadow-lg hover:scale-105 transition-transform"
      >
        <Bot className="w-6 h-6" strokeWidth={1.5} />
      </button>

      {open && (
        <div className="fixed bottom-6 right-6 z-50 w-[calc(100vw-3rem)] sm:w-96 h-[560px] max-h-[80vh] bg-card border border-border flex flex-col shadow-2xl" data-testid="ai-panel">
          <div className="flex items-center justify-between px-4 py-3 bg-primary text-primary-foreground">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4" />
              <span className="font-heading font-semibold text-sm">Portfolio AI Analyst</span>
            </div>
            <button onClick={() => setOpen(false)} data-testid="ai-close"><X className="w-4 h-4" /></button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-muted/30">
            {msgs.map((m) => (
              <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-3 py-2 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-primary text-primary-foreground" : "bg-card border border-border font-mono text-xs leading-relaxed"}`}>
                  {m.text}
                </div>
              </div>
            ))}
            {loading && <div className="text-xs text-muted-foreground animate-pulse">Analyzing portfolio…</div>}
            {msgs.length === 1 && (
              <div className="space-y-2 pt-2">
                {suggestions.map((s) => (
                  <button key={s} onClick={() => setInput(s)} className="block w-full text-left text-xs px-3 py-2 border border-border hover:bg-card transition-colors">{s}</button>
                ))}
              </div>
            )}
            <div ref={endRef} />
          </div>

          <div className="p-3 border-t border-border flex gap-2">
            <input
              data-testid="ai-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Ask about your portfolio..."
              className="flex-1 px-3 py-2 text-sm border border-input bg-background focus:outline-none focus:ring-1 focus:ring-primary"
            />
            <button onClick={send} disabled={loading} data-testid="ai-send" className="bg-primary text-primary-foreground px-3 disabled:opacity-50">
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
